import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

export default function Cookies() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <Helmet>
        <title>{t("legal.cookies.metaTitle")}</title>
        <meta name="description" content={t("legal.cookies.metaDesc")} />
        <link rel="canonical" href="https://tipomatic.app/cookies" />
        <meta property="og:title" content={t("legal.cookies.metaTitle")} />
        <meta property="og:description" content={t("legal.cookies.metaDesc")} />
        <meta property="og:url" content="https://tipomatic.app/cookies" />
      </Helmet>

      <header className="container mx-auto px-4 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold text-lg">
          <img src="/icon-512.png?v=2" alt="Tipomatic" className="h-8 w-8 rounded-lg object-cover" />
          Tipomatic
        </div>
        <Button asChild variant="ghost" className="text-pitch-cream hover:bg-white/10 hover:text-pitch-cream">
          <Link to="/"><ChevronLeft className="mr-1 h-4 w-4" /> {t("legal.back")}</Link>
        </Button>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold mb-8">{t("legal.cookies.title")}</h1>

        <div className="space-y-8 text-pitch-cream/80 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-pitch-cream mb-2">{t("legal.cookies.s1Title")}</h2>
            <p>{t("legal.cookies.s1Body")}</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-pitch-cream mb-2">{t("legal.cookies.s2Title")}</h2>
            <p>{t("legal.cookies.s2Body")}</p>
            <h3 className="text-base font-semibold text-pitch-cream mt-4 mb-2">{t("legal.cookies.s2TableTitle")}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-pitch-cream/20">
                    <th className="py-2 pr-4 font-semibold text-pitch-cream">{t("legal.cookies.s2TableColName")}</th>
                    <th className="py-2 pr-4 font-semibold text-pitch-cream">{t("legal.cookies.s2TableColType")}</th>
                    <th className="py-2 pr-4 font-semibold text-pitch-cream">{t("legal.cookies.s2TableColDuration")}</th>
                    <th className="py-2 font-semibold text-pitch-cream">{t("legal.cookies.s2TableColPurpose")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t("legal.cookies.s2Table", { returnObjects: true }) as Array<{ name: string; type: string; duration: string; purpose: string }>).map((row, i) => (
                    <tr key={i} className="border-b border-pitch-cream/10">
                      <td className="py-2 pr-4 align-top">{row.name}</td>
                      <td className="py-2 pr-4 align-top">{row.type}</td>
                      <td className="py-2 pr-4 align-top">{row.duration}</td>
                      <td className="py-2 align-top">{row.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-pitch-cream mb-2">{t("legal.cookies.s3Title")}</h2>
            <p>{t("legal.cookies.s3Body")}</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-pitch-cream mb-2">{t("legal.cookies.s4Title")}</h2>
            <p>{t("legal.cookies.s4Body")}</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-pitch-cream mb-2">{t("legal.cookies.s5Title")}</h2>
            <p>
              {t("legal.cookies.s5BodyPrefix")}
              <a href="mailto:notify@tipomatic.app" className="text-primary underline">notify@tipomatic.app</a>.
            </p>
          </section>
        </div>
      </main>

      <Footer variant="dark" />
    </div>
  );
}
